// Filter key → locale dictionary key mapping.
// Extend this record when new filter keys appear from the backend.
const FILTER_KEY_LABELS: Record<string, string> = {
  size: "filter.key.size",
  color_code: "filter.key.color",
  brand: "filter.key.brand",
  material: "filter.key.material",
  model: "filter.key.model",
  category: "filter.key.category",
  fabric_quality: "filter.key.fabric_quality",
  price: "filter.key.price",
  variant_number: "filter.key.variant_number",
};

const COLOR_FILTER_KEYS = new Set(["color_code"]);

export function isColorFilter(filterKey: string): boolean {
  return COLOR_FILTER_KEYS.has(filterKey);
}

// Normalizes a raw backend filter option value for display:
// underscores become spaces and the first character is capitalized.
export function formatOptionLabel(raw: string): string {
  const spaced = raw.replace(/_/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function getFilterKeyLabel(filterKey: string, t: (key: string) => string): string {
  const localeKey = FILTER_KEY_LABELS[filterKey];
  if (localeKey) {
    const translated = t(localeKey);
    if (translated !== localeKey) return translated;
  }
  return filterKey.charAt(0).toUpperCase() + filterKey.slice(1);
}

// --- Hex color classification ---

function parseHex(raw: string): [number, number, number] | null {
  const hex = raw.replace(/^#/, "");
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
  return [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16),
  ];
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h * 360, s, l];
}

type ColorBucket =
  | "black" | "white" | "grey"
  | "red" | "orange" | "yellow" | "green" | "blue" | "dark_blue" | "purple" | "pink"
  | "brown" | "beige";

export function classifyHex(raw: string): ColorBucket {
  const rgb = parseHex(raw);
  if (!rgb) return "grey";
  const [h, s, l] = rgbToHsl(...rgb);

  // Neutrals
  if (s < 0.10) {
    if (l < 0.15) return "black";
    if (l > 0.85) return "white";
    return "grey";
  }

  // Brown / beige: warm hues with low-to-mid lightness
  if (h >= 15 && h < 45 && (s < 0.55 || l < 0.4)) {
    return l < 0.45 ? "brown" : "beige";
  }

  // Chromatic
  if (h < 15 || h >= 345) return "red";
  if (h < 45) return "orange";
  if (h < 70) return "yellow";
  if (h < 170) return "green";
  if (h < 260) return l < 0.35 ? "dark_blue" : "blue";
  if (h < 300) return "purple";
  return "pink";
}

export function normalizeHex(raw: string): string {
  const cleaned = raw.replace(/^#/, "");
  return `#${cleaned}`;
}

export function getColorLabel(hex: string, t: (key: string) => string): string {
  const bucket = classifyHex(hex);
  const localeKey = `filter.color.${bucket}`;
  const translated = t(localeKey);
  if (translated !== localeKey) return translated;
  return bucket.charAt(0).toUpperCase() + bucket.slice(1);
}
